<script setup lang="ts">
import { loginStations, nextLoginStationId } from '~/utils/login-stations'

definePageMeta({
  layout: false
})

useSeoMeta({
  title: 'Iniciar sesión · Industrias Monzón',
  description: 'Acceso al registro de planta de Industrias Monzón: inventario, telas y movimientos del taller de bordado.'
})

const activeId = ref(loginStations[0]?.id ?? 'bastidor')
const hovering = ref(false)
const locked = ref(false)
const panel = ref<HTMLElement | null>(null)

const active = computed(() => loginStations.find(station => station.id === activeId.value) ?? loginStations[0])

let timer: ReturnType<typeof setInterval> | undefined

function selectStation(id: string, lock = false) {
  activeId.value = id
  if (lock) locked.value = true
}

function onStationKeydown(event: KeyboardEvent, index: number) {
  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
  event.preventDefault()
  const direction = event.key === 'ArrowRight' ? 1 : -1
  const current = loginStations[index]
  if (!current) return
  const nextId = nextLoginStationId(loginStations, current.id, direction)
  if (!nextId) return
  selectStation(nextId, true)
  document.getElementById(`login-station-${nextId}`)?.focus()
}

function isPanelActive() {
  const active = document.activeElement
  return !!(panel.value && active instanceof Node && panel.value.contains(active))
}

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  timer = setInterval(() => {
    if (hovering.value || locked.value || isPanelActive()) return
    const nextId = nextLoginStationId(loginStations, activeId.value, 1)
    if (nextId) activeId.value = nextId
  }, 6800)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="min-h-[100dvh] bg-[#f6f3ee] lg:grid lg:h-[100dvh] lg:grid-cols-[minmax(0,1.45fr)_minmax(20rem,32rem)] lg:overflow-hidden dark:bg-[#161412]">
    <LoginCover
      v-model="activeId"
      :stations="loginStations"
      @hover="hovering = $event"
      @lock="locked = true"
    />

    <div
      ref="panel"
      class="flex flex-col justify-center px-6 py-10 sm:px-10 lg:overflow-y-auto lg:px-12"
      @pointerenter="hovering = true"
      @pointerleave="hovering = false"
    >
      <div class="mx-auto w-full max-w-[26rem] space-y-8">
        <div class="space-y-5">
          <div class="flex items-center gap-3" aria-hidden="true">
            <span class="h-1 w-7 rounded-full bg-[#c4453c]" />
            <span class="h-1 w-4 rounded-full bg-[#c6a15a]" />
            <span class="h-1 w-9 rounded-full bg-[#2f6f9f]" />
            <span class="h-1 w-3 rounded-full bg-[#2a2724] dark:bg-zinc-300" />
          </div>

          <div class="space-y-3">
            <p class="text-[0.68rem] font-medium tracking-[0.22em] text-primary uppercase">
              Industrias Monzón
            </p>
            <h1 class="max-w-[12ch] text-4xl leading-none font-medium tracking-tight text-zinc-900 dark:text-zinc-50">
              El registro del taller.
            </h1>
          </div>

          <div
            role="radiogroup"
            aria-label="Detalles del taller"
            class="flex gap-5"
            @pointerenter="hovering = true"
          >
            <button
              v-for="(station, index) in loginStations"
              :id="`login-station-${station.id}`"
              :key="station.id"
              type="button"
              role="radio"
              :aria-checked="activeId === station.id"
              class="border-b-2 pb-1 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary active:translate-y-px"
              :class="activeId === station.id
                ? 'border-primary font-medium text-zinc-900 dark:text-zinc-50'
                : 'border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'"
              @click="selectStation(station.id, true)"
              @keydown="onStationKeydown($event, index)"
              @focus="selectStation(station.id, true)"
            >
              <span class="mr-1.5 text-[0.65rem] tracking-[0.16em] text-zinc-400">{{ station.index }}</span>
              {{ station.title }}
            </button>
          </div>

          <Transition name="login-station" mode="out-in">
            <p
              :key="active?.id"
              class="min-h-[4.5rem] max-w-[38ch] text-sm leading-relaxed text-zinc-600 dark:text-zinc-400"
              aria-live="polite"
            >
              {{ active?.caption }}
            </p>
          </Transition>
        </div>

        <div class="h-px bg-zinc-900/10 dark:bg-white/10" />

        <AuthForm />
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-station-enter-active,
.login-station-leave-active {
  transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.login-station-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.login-station-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .login-station-enter-active,
  .login-station-leave-active {
    transition: none;
  }
}
</style>
